/**
 * Ysgithyrwyn stirs (M5-04) — the Great Hunt's escalation, on M5-02's scheduler.
 *
 * The boar is at his wallow from the day the valley is raised; the escalation is
 * not about *making* him exist but about the valley starting to *notice* him.
 * That is the whole of the arc's opening, and the ticket is emphatic that it
 * takes no quest UI: the world produces sign and the Elder repeats it, and a
 * player who has never been told there is a boss follows an old man's worry up
 * the ford and finds one.
 *
 * So there is no script here, exactly as there is none in `raids.ts`. What is
 * here is a scheduler rule gated on the calendar — armed for the playtest's
 * third week, or by an admin (M7-05) — that, once the chief is stirring, writes
 * a `sign` happening into the chronicle on a cooldown. The Elder reads it back
 * out with words content already wrote (the `sign` pool in `rumours.ts`, whose
 * first line is Old Cadog and the oaks torn to shoulder height), and the trail a
 * player then walks is the boar's own ordinary movement sign (M3-08), because a
 * boar the size of a pony leaves a boar the size of a pony's tracks.
 *
 * The one fact it reads off the world is the `apex` flag on an instinct, so the
 * sim never names Ysgithyrwyn: the rule stirs whatever the valley's apex animal
 * is, and falls silent the moment that animal is dead — which is the right
 * ending, because the news of a monster in the wood should stop when the monster
 * does.
 */
import { gameTime, TICKS_PER_GAME_HOUR } from './time.js';
/* -------------------------------------------------------------------------- *
 * The numbers
 * -------------------------------------------------------------------------- */
/**
 * The game-day the chief begins to stir.
 *
 * Fifteen: the start of the third game-week, which is the ticket's "playtest's
 * third week" read on the game calendar (a game day is a real hour, so this is
 * the escalation ramping over a run rather than a wall-clock fortnight). It is a
 * constant rather than config because the admin-armed override is M7-05's, and a
 * number here is a number a reviewer can see and move.
 */
export const BOAR_STIRS_DAY = 15;
/** Firings of the sign rule allowed in a week, and the gap between them. */
export const BOAR_SIGN_PER_WEEK = 6;
export const BOAR_SIGN_COOLDOWN = TICKS_PER_GAME_HOUR * 18;
/**
 * Chance per hour that fresh sign turns up, once he is stirring.
 *
 * Low, and quoted per hour like every other pressure: what it buys is that a
 * player who asks the Elder across a few game-days hears the wood mentioned more
 * than once, so the sign reads as a *standing* thing about the third week and
 * not a single line they might have missed.
 */
export const BOAR_SIGN_CHANCE = 0.08;
/* -------------------------------------------------------------------------- *
 * Finding the apex
 * -------------------------------------------------------------------------- */
/**
 * The apex animal's actor id, if it is alive and standing in the world.
 *
 * Read off the `apex` instinct flag rather than a species id, so this file knows
 * nothing about boars. Sorted for determinism, though there is only ever one.
 */
export function apexBeast(ctx) {
    for (const id of Object.keys(ctx.world.minds).sort()) {
        const mind = ctx.world.minds[id];
        if (!mind)
            continue;
        if (!ctx.catalogue.instinct?.(mind.instinctId)?.apex)
            continue;
        if (!ctx.world.actors[id])
            continue;
        if (ctx.world.combat[id]?.vitals.dead)
            continue;
        return id;
    }
    return null;
}
/* -------------------------------------------------------------------------- *
 * The rule
 * -------------------------------------------------------------------------- */
export const boarSignRule = {
    id: 'boar-sign',
    perWeek: BOAR_SIGN_PER_WEEK,
    cooldown: BOAR_SIGN_COOLDOWN,
    when(ctx) {
        // Armed by the calendar. Before the third week the boar is just a boar in
        // the deep wood, and the valley has nothing to say about him.
        if (gameTime(ctx.tick).day < BOAR_STIRS_DAY)
            return false;
        return apexBeast(ctx) !== null;
    },
    chance() {
        return BOAR_SIGN_CHANCE;
    },
    fire(ctx) {
        const id = apexBeast(ctx);
        const actor = id ? ctx.world.actors[id] : undefined;
        if (!actor)
            return;
        // Archived, not narrated: nobody is standing in the deep oakwood at the
        // moment the sign is made, and a player who was *told* about it in their log
        // would never have to go and ask. What surfaces it is the Elder, reading
        // this out of the chronicle with the words in `rumours.ts` — the same path a
        // wolf raid reaches the village by.
        ctx.emit({
            type: 'status',
            actors: [actor.id],
            refs: ['sign', 'boar'],
            text: 'The oaks past the ford are torn to shoulder height, and the ground beneath them churned.',
            audience: 'archive',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/** Everything M5-04's escalation installs on the scheduler. */
export const BOAR_PRESSURE_RULES = [boarSignRule];
//# sourceMappingURL=boar.js.map